/**
 * Trovva — Action Plans Tab
 *
 * Two views:
 *   1. Plan List — cards showing all templates with step count, enrolled count, category
 *   2. Plan Builder — click a plan to see/edit steps, manage enrollments
 *
 * Step types: email, sms, task, wait, update_field, notify, enroll, webhook
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Plus, ChevronRight, Mail, MessageSquare, Clock, CheckCircle2,
  Loader2, Trash2, Edit2, Pause, Play, X, AlertCircle, Users,
  RefreshCw, ArrowRight, Briefcase, Bell, Tag as TagIcon,
  Zap, Globe, FileText,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { theme } from '../../lib/theme';

const STEP_TYPES = [
  { value: 'email',        label: 'Email',        icon: Mail,           color: '#3B82F6', desc: 'Send email via Gmail/Outlook' },
  { value: 'sms',          label: 'SMS',           icon: MessageSquare,  color: '#22C55E', desc: 'Send text via Twilio' },
  { value: 'task',         label: 'Task',          icon: CheckCircle2,   color: '#F97316', desc: 'Create task for agent' },
  { value: 'wait',         label: 'Wait',          icon: Clock,          color: '#8B5CF6', desc: 'Pause before next step' },
  { value: 'notify',       label: 'Notify',        icon: Bell,           color: '#EAB308', desc: 'In-app notification to agent' },
  { value: 'update_field', label: 'Update Field',  icon: Edit2,          color: '#06B6D4', desc: 'Change a contact field' },
  { value: 'enroll',       label: 'Enroll in Plan', icon: ArrowRight,    color: '#EC4899', desc: 'Chain to another plan' },
  { value: 'webhook',      label: 'Webhook',       icon: Globe,          color: '#64748B', desc: 'Fire external webhook' },
];

const CATEGORIES = [
  { value: 'general',      label: 'General' },
  { value: 'buyer',        label: 'Buyer' },
  { value: 'seller',       label: 'Seller' },
  { value: 'past_client',  label: 'Past Client' },
  { value: 'sphere',       label: 'Sphere' },
];

const TRIGGER_OPTIONS = [
  { value: 'manual',              label: 'Manual Only' },
  { value: 'lead_created',        label: 'New Lead Created' },
  { value: 'lead_status_change',  label: 'Lead Status Changes' },
  { value: 'deal_stage_change',   label: 'Deal Stage Changes' },
];

export default function ActionPlansTab({ demoMode, showToast }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePlanId, setActivePlanId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    if (demoMode) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');
      const r = await fetch('/api/action-plans', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed');
      setTemplates(data.templates || []);
    } catch (e) {
      showToast?.(e.message);
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoMode]);

  useEffect(() => { load(); }, [load]);

  if (activePlanId) {
    const tmpl = templates.find(t => t.id === activePlanId);
    return (
      <PlanBuilder
        template={tmpl}
        onBack={() => { setActivePlanId(null); load(); }}
        showToast={showToast}
      />
    );
  }

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: theme.colors.text }}>
          Action Plans
        </h3>
        <span style={{ fontSize: 12, color: theme.colors.textLight }}>
          Drip sequences that run on autopilot
        </span>
        <div style={{ flex: 1 }} />
        <button onClick={load} title="Refresh" style={toolBtn()}>
          <RefreshCw size={13} />
        </button>
        <button onClick={() => setShowCreate(true)} style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '7px 14px', borderRadius: 8, border: 'none',
          background: theme.colors.primary, color: 'white',
          fontSize: 12, fontWeight: 600, cursor: 'pointer',
        }}>
          <Plus size={14} /> New Plan
        </button>
      </div>

      {/* Create modal */}
      {showCreate && (
        <CreatePlanModal
          onClose={() => setShowCreate(false)}
          onCreated={(t) => { setTemplates(prev => [t, ...prev]); setShowCreate(false); setActivePlanId(t.id); }}
          showToast={showToast}
        />
      )}

      {/* Loading */}
      {loading && (
        <div style={{ padding: 40, textAlign: 'center', color: theme.colors.textLight, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading plans…
        </div>
      )}

      {/* Empty state */}
      {!loading && templates.length === 0 && (
        <div style={{ padding: '50px 20px', textAlign: 'center' }}>
          <div style={{
            width: 56, height: 56, borderRadius: 28, background: `${theme.colors.primary}14`,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
          }}>
            <Zap size={24} color={theme.colors.primary} />
          </div>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text, margin: '0 0 6px' }}>
            No Action Plans Yet
          </h3>
          <p style={{ fontSize: 13, color: theme.colors.textLight, maxWidth: 400, margin: '0 auto 16px' }}>
            Create your first drip sequence — a multi-step plan that automatically follows up with leads via email, text, and tasks.
          </p>
          <button onClick={() => setShowCreate(true)} style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '9px 18px', borderRadius: 8, border: 'none',
            background: theme.colors.primary, color: 'white',
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>
            <Plus size={14} /> Create First Plan
          </button>
        </div>
      )}

      {/* Plan cards grid */}
      {!loading && templates.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
          {templates.map(t => (
            <PlanCard key={t.id} template={t} onClick={() => setActivePlanId(t.id)} />
          ))}
        </div>
      )}

      <style>{`@keyframes spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// Plan Card
// ══════════════════════════════════════════════════════════════
function PlanCard({ template: t, onClick }) {
  const catColor = CATEGORIES.find(c => c.value === t.category)?.label || 'General';
  const stats = t.enrollment_stats || { active: 0, completed: 0, total: 0 };
  const stepTypes = [...new Set((t.steps || []).map(s => s.step_type))];

  return (
    <div onClick={onClick} style={{
      padding: 16, borderRadius: 12,
      border: `1px solid ${theme.colors.border}`, background: theme.colors.card,
      cursor: 'pointer', transition: 'box-shadow 0.15s, border-color 0.15s',
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = theme.colors.primary; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = theme.colors.border; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{
          width: 38, height: 38, borderRadius: 10, flexShrink: 0,
          background: `${theme.colors.primary}12`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Zap size={18} color={theme.colors.primary} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text }}>{t.name}</span>
            {!t.is_active && (
              <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: '#FEE2E2', color: '#DC2626' }}>PAUSED</span>
            )}
          </div>
          {t.description && (
            <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {t.description}
            </div>
          )}
        </div>
        <ChevronRight size={16} color={theme.colors.textLight} />
      </div>

      {/* Meta row */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <MetaChip icon={<FileText size={10} />} label={`${t.step_count || (t.steps || []).length} steps`} />
        <MetaChip icon={<Users size={10} />} label={`${stats.active} active`} color={stats.active > 0 ? theme.colors.success : undefined} />
        {t.category !== 'general' && <MetaChip label={catColor} />}
        {t.trigger_on !== 'manual' && <MetaChip icon={<Zap size={10} />} label="Auto" color={theme.colors.warning} />}
      </div>

      {/* Step type icons */}
      {stepTypes.length > 0 && (
        <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
          {stepTypes.map(st => {
            const meta = STEP_TYPES.find(s => s.value === st);
            if (!meta) return null;
            const Icon = meta.icon;
            return (
              <div key={st} title={meta.label} style={{
                width: 24, height: 24, borderRadius: 6,
                background: `${meta.color}12`, color: meta.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon size={12} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MetaChip({ icon, label, color }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      padding: '2px 7px', borderRadius: 6, fontSize: 10, fontWeight: 600,
      background: color ? `${color}12` : theme.colors.bg,
      color: color || theme.colors.textLight,
    }}>
      {icon} {label}
    </span>
  );
}


// ══════════════════════════════════════════════════════════════
// Create Plan Modal
// ══════════════════════════════════════════════════════════════
function CreatePlanModal({ onClose, onCreated, showToast }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general');
  const [triggerOn, setTriggerOn] = useState('manual');
  const [stopOnReply, setStopOnReply] = useState(true);
  const [creating, setCreating] = useState(false);

  const create = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/action-plans', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), description, category, trigger_on: triggerOn, stop_on_reply: stopOnReply }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed');
      showToast?.('Plan created');
      onCreated(data.template);
    } catch (e) {
      showToast?.(e.message);
    } finally { setCreating(false); }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 500,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: '100%', maxWidth: 480, background: theme.colors.card,
        borderRadius: 14, padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: theme.colors.text }}>Create Action Plan</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.textLight }}><X size={18} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={labelStyle()}>Plan Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., New Buyer 90-Day Drip"
              style={inputStyle()} autoFocus onKeyDown={e => { if (e.key === 'Enter') create(); }} />
          </div>
          <div>
            <label style={labelStyle()}>Description</label>
            <input value={description} onChange={e => setDescription(e.target.value)} placeholder="What this plan does…"
              style={inputStyle()} />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle()}>Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)} style={inputStyle()}>
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle()}>Trigger</label>
              <select value={triggerOn} onChange={e => setTriggerOn(e.target.value)} style={inputStyle()}>
                {TRIGGER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: theme.colors.text, cursor: 'pointer' }}>
            <input type="checkbox" checked={stopOnReply} onChange={e => setStopOnReply(e.target.checked)} />
            Stop plan when contact replies
          </label>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onClose} style={{
            padding: '8px 16px', borderRadius: 8, border: `1px solid ${theme.colors.border}`,
            background: theme.colors.card, color: theme.colors.text, fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}>Cancel</button>
          <button onClick={create} disabled={creating || !name.trim()} style={{
            padding: '8px 16px', borderRadius: 8, border: 'none',
            background: theme.colors.primary, color: 'white', fontSize: 12, fontWeight: 600,
            cursor: creating ? 'wait' : 'pointer', opacity: (creating || !name.trim()) ? 0.5 : 1,
            display: 'inline-flex', alignItems: 'center', gap: 5,
          }}>
            {creating ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Plus size={12} />}
            Create Plan
          </button>
        </div>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// Plan Builder — step editor + enrollment list
// ══════════════════════════════════════════════════════════════
function PlanBuilder({ template, onBack, showToast }) {
  const [steps, setSteps] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingStep, setAddingStep] = useState(false);
  const [view, setView] = useState('steps'); // 'steps' | 'enrollments'

  const loadData = useCallback(async () => {
    if (!template?.id) return;
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const headers = { Authorization: `Bearer ${session.access_token}` };

      const [stepsRes, enrollRes] = await Promise.all([
        fetch(`/api/action-plans?id=${template.id}&steps=1`, { headers }),
        fetch(`/api/action-plans?enrollments=1&template_id=${template.id}&limit=50`, { headers }),
      ]);
      const stepsData = await stepsRes.json();
      const enrollData = await enrollRes.json();

      if (stepsRes.ok) setSteps(stepsData.steps || []);
      if (enrollRes.ok) setEnrollments(enrollData.enrollments || []);
    } catch (e) {
      showToast?.(e.message);
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  const deleteStep = async (stepId) => {
    if (!window.confirm('Delete this step?')) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`/api/action-plans?step_id=${stepId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      showToast?.('Step deleted');
      loadData();
    } catch (e) { showToast?.(e.message); }
  };

  const updateStep = async (stepId, patch) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`/api/action-plans?step_id=${stepId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(patch),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      showToast?.('Step updated');
      loadData();
    } catch (e) { showToast?.(e.message); throw e; }
  };

  const togglePlanActive = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`/api/action-plans?id=${template.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !template.is_active }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      showToast?.(template.is_active ? 'Plan paused' : 'Plan activated');
      onBack();
    } catch (e) { showToast?.(e.message); }
  };

  const deletePlan = async () => {
    if (!window.confirm(`Delete "${template.name}"? This will cancel all active enrollments.`)) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`/api/action-plans?id=${template.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      showToast?.('Plan deleted');
      onBack();
    } catch (e) { showToast?.(e.message); }
  };

  const updateEnrollmentStatus = async (enrollId, status) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`/api/action-plans?enrollment_id=${enrollId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      showToast?.(`Enrollment ${status}`);
      loadData();
    } catch (e) { showToast?.(e.message); }
  };

  if (!template) return null;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={onBack} style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '6px 12px', borderRadius: 8, border: `1px solid ${theme.colors.border}`,
          background: theme.colors.card, cursor: 'pointer', fontSize: 12, fontWeight: 600,
          color: theme.colors.textLight,
        }}>
          <ChevronRight size={12} style={{ transform: 'rotate(180deg)' }} /> Back
        </button>
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: theme.colors.text }}>{template.name}</h3>
          {template.description && <div style={{ fontSize: 12, color: theme.colors.textLight }}>{template.description}</div>}
        </div>
        <button onClick={togglePlanActive} style={{
          ...toolBtn(),
          color: template.is_active ? theme.colors.warning : theme.colors.success,
          display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600,
        }}>
          {template.is_active ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Activate</>}
        </button>
        <button onClick={deletePlan} style={{ ...toolBtn(), color: theme.colors.danger, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600 }}>
          <Trash2 size={12} /> Delete
        </button>
      </div>

      {/* Tabs: Steps | Enrollments */}
      <div style={{ display: 'flex', gap: 0, background: '#EEF2F7', borderRadius: 8, padding: 2, marginBottom: 16, width: 'fit-content' }}>
        {[
          { key: 'steps', label: 'Steps', count: steps.length },
          { key: 'enrollments', label: 'Enrollments', count: enrollments.length },
        ].map(t => {
          const active = view === t.key;
          return (
            <button key={t.key} onClick={() => setView(t.key)} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '6px 14px', border: 'none', borderRadius: 6,
              background: active ? theme.colors.card : 'transparent',
              color: active ? theme.colors.text : theme.colors.textLight,
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
              boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
            }}>
              {t.label}
              {t.count > 0 && <span style={{
                background: active ? theme.colors.primary : theme.colors.textLight,
                color: 'white', borderRadius: 8, padding: '1px 6px', fontSize: 10, fontWeight: 700,
              }}>{t.count}</span>}
            </button>
          );
        })}
      </div>

      {loading && (
        <div style={{ padding: 40, textAlign: 'center', color: theme.colors.textLight, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading…
        </div>
      )}

      {/* ── STEPS VIEW ── */}
      {!loading && view === 'steps' && (
        <div>
          {steps.length === 0 && !addingStep && (
            <div style={{ padding: '30px 20px', textAlign: 'center', color: theme.colors.textLight }}>
              <p style={{ fontSize: 13, margin: '0 0 12px' }}>No steps yet. Add your first step to build the drip sequence.</p>
            </div>
          )}

          {/* Step timeline */}
          {steps.map((step, idx) => (
            <StepRow key={step.id} step={step} index={idx} isLast={idx === steps.length - 1} onDelete={() => deleteStep(step.id)} onUpdate={updateStep} />
          ))}

          {/* Add step */}
          {addingStep ? (
            <AddStepForm
              templateId={template.id}
              nextOrder={steps.length + 1}
              onAdded={() => { setAddingStep(false); loadData(); }}
              onCancel={() => setAddingStep(false)}
              showToast={showToast}
            />
          ) : (
            <button onClick={() => setAddingStep(true)} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '10px 16px', borderRadius: 10, width: '100%',
              border: `2px dashed ${theme.colors.border}`, background: 'transparent',
              cursor: 'pointer', fontSize: 12, fontWeight: 600, color: theme.colors.primary,
              marginTop: steps.length > 0 ? 8 : 0,
            }}>
              <Plus size={14} /> Add Step
            </button>
          )}
        </div>
      )}

      {/* ── ENROLLMENTS VIEW ── */}
      {!loading && view === 'enrollments' && (
        <div>
          {enrollments.length === 0 ? (
            <div style={{ padding: '30px 20px', textAlign: 'center', color: theme.colors.textLight }}>
              <p style={{ fontSize: 13 }}>No contacts enrolled in this plan yet.</p>
              <p style={{ fontSize: 12 }}>Enroll contacts from their detail drawer or via automations.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {enrollments.map(e => (
                <EnrollmentRow key={e.id} enrollment={e} onStatusChange={updateEnrollmentStatus} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// Step Row (timeline-style)
// ══════════════════════════════════════════════════════════════
function StepRow({ step, index, isLast, onDelete, onUpdate }) {
  const meta = STEP_TYPES.find(s => s.value === step.step_type) || STEP_TYPES[0];
  const Icon = meta.icon;
  const delay = formatDelay(step);
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editSubject, setEditSubject] = useState(step.subject || '');
  const [editBody, setEditBody] = useState(step.body || '');
  const [editTaskTitle, setEditTaskTitle] = useState(step.task_title || '');
  const [editFieldName, setEditFieldName] = useState(step.field_name || '');
  const [editFieldValue, setEditFieldValue] = useState(step.field_value || '');
  const [editDelayDays, setEditDelayDays] = useState(step.delay_days || 0);
  const [editDelayHours, setEditDelayHours] = useState(step.delay_hours || 0);
  const [saving, setSaving] = useState(false);

  const hasLongContent = (step.body || '').length > 120;

  const handleSave = async () => {
    setSaving(true);
    const patch = { delay_days: parseInt(editDelayDays) || 0, delay_hours: parseInt(editDelayHours) || 0 };
    if (step.step_type === 'email') { patch.subject = editSubject; patch.body = editBody; }
    else if (['sms', 'whatsapp', 'notify'].includes(step.step_type)) { patch.body = editBody; }
    else if (step.step_type === 'task') { patch.task_title = editTaskTitle; patch.body = editBody; }
    else if (step.step_type === 'update_field') { patch.field_name = editFieldName; patch.field_value = editFieldValue; }
    await onUpdate?.(step.id, patch);
    setSaving(false);
    setEditing(false);
  };

  return (
    <div style={{ display: 'flex', gap: 12, position: 'relative' }}>
      {/* Timeline line */}
      {!isLast && (
        <div style={{
          position: 'absolute', left: 17, top: 38, bottom: -8, width: 2,
          background: theme.colors.border,
        }} />
      )}

      {/* Step icon */}
      <div style={{
        width: 36, height: 36, borderRadius: 18, flexShrink: 0,
        background: `${meta.color}14`, color: meta.color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: `2px solid ${meta.color}40`,
        zIndex: 1,
      }}>
        <Icon size={16} />
      </div>

      {/* Step content */}
      <div style={{
        flex: 1, padding: '10px 14px', borderRadius: 10, marginBottom: 8,
        border: `1px solid ${editing ? theme.colors.primary : theme.colors.border}`,
        background: theme.colors.card,
        transition: 'border-color 0.15s',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: meta.color, textTransform: 'uppercase' }}>
            Step {index + 1} · {meta.label}
          </span>
          {delay && (
            <span style={{ fontSize: 10, color: theme.colors.textLight, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              <Clock size={9} /> {delay}
            </span>
          )}
          <div style={{ flex: 1 }} />
          {!editing && (
            <button onClick={() => setEditing(true)} title="Edit step" style={{
              background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.primary,
              padding: 2, display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 600,
            }}>
              <Edit2 size={11} /> Edit
            </button>
          )}
          <button onClick={onDelete} title="Delete step" style={{
            background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.textLight,
            padding: 2,
          }}>
            <Trash2 size={12} />
          </button>
        </div>

        {/* ── EDIT MODE ── */}
        {editing ? (
          <div style={{ marginTop: 8 }}>
            {/* Delay editor */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <div>
                <label style={{ fontSize: 10, fontWeight: 600, color: theme.colors.textLight }}>Delay (days)</label>
                <input type="number" min={0} value={editDelayDays} onChange={e => setEditDelayDays(e.target.value)}
                  style={{ width: 60, padding: '5px 8px', borderRadius: 6, border: `1px solid ${theme.colors.border}`, fontSize: 12, marginTop: 2 }} />
              </div>
              <div>
                <label style={{ fontSize: 10, fontWeight: 600, color: theme.colors.textLight }}>Hours</label>
                <input type="number" min={0} max={23} value={editDelayHours} onChange={e => setEditDelayHours(e.target.value)}
                  style={{ width: 60, padding: '5px 8px', borderRadius: 6, border: `1px solid ${theme.colors.border}`, fontSize: 12, marginTop: 2 }} />
              </div>
            </div>

            {step.step_type === 'email' && (
              <>
                <label style={{ fontSize: 10, fontWeight: 600, color: theme.colors.textLight }}>Subject</label>
                <input value={editSubject} onChange={e => setEditSubject(e.target.value)}
                  style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: `1px solid ${theme.colors.border}`, fontSize: 12, marginTop: 2, marginBottom: 8, boxSizing: 'border-box' }} />
                <label style={{ fontSize: 10, fontWeight: 600, color: theme.colors.textLight }}>Body</label>
                <textarea value={editBody} onChange={e => setEditBody(e.target.value)} rows={8}
                  style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: `1px solid ${theme.colors.border}`, fontSize: 12, marginTop: 2, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
              </>
            )}
            {['sms', 'whatsapp', 'notify'].includes(step.step_type) && (
              <>
                <label style={{ fontSize: 10, fontWeight: 600, color: theme.colors.textLight }}>Message</label>
                <textarea value={editBody} onChange={e => setEditBody(e.target.value)} rows={4}
                  style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: `1px solid ${theme.colors.border}`, fontSize: 12, marginTop: 2, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
              </>
            )}
            {step.step_type === 'task' && (
              <>
                <label style={{ fontSize: 10, fontWeight: 600, color: theme.colors.textLight }}>Task Title</label>
                <input value={editTaskTitle} onChange={e => setEditTaskTitle(e.target.value)}
                  style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: `1px solid ${theme.colors.border}`, fontSize: 12, marginTop: 2, marginBottom: 8, boxSizing: 'border-box' }} />
              </>
            )}
            {step.step_type === 'update_field' && (
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 10, fontWeight: 600, color: theme.colors.textLight }}>Field</label>
                  <input value={editFieldName} onChange={e => setEditFieldName(e.target.value)}
                    style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: `1px solid ${theme.colors.border}`, fontSize: 12, marginTop: 2, boxSizing: 'border-box' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 10, fontWeight: 600, color: theme.colors.textLight }}>Value</label>
                  <input value={editFieldValue} onChange={e => setEditFieldValue(e.target.value)}
                    style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: `1px solid ${theme.colors.border}`, fontSize: 12, marginTop: 2, boxSizing: 'border-box' }} />
                </div>
              </div>
            )}

            {/* Merge field hints */}
            <div style={{ fontSize: 10, color: theme.colors.textLight, marginTop: 6, padding: '4px 8px', background: theme.colors.bg, borderRadius: 6 }}>
              Merge fields: {'{{first_name}}'} {'{{last_name}}'} {'{{agent_name}}'} {'{{agent_phone}}'} {'{{brokerage_name}}'} {'{{lead_source}}'} {'{{budget}}'}
            </div>

            {/* Save / Cancel */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
              <button onClick={() => setEditing(false)} style={{
                padding: '5px 12px', borderRadius: 6, border: `1px solid ${theme.colors.border}`,
                background: theme.colors.card, cursor: 'pointer', fontSize: 11, fontWeight: 600, color: theme.colors.textLight,
              }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{
                padding: '5px 12px', borderRadius: 6, border: 'none',
                background: theme.colors.primary, color: 'white', cursor: saving ? 'wait' : 'pointer',
                fontSize: 11, fontWeight: 600, opacity: saving ? 0.6 : 1,
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}>
                {saving ? <Loader2 size={10} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={10} />}
                Save
              </button>
            </div>
          </div>
        ) : (
          /* ── READ MODE (with expand/collapse) ── */
          <>
            {step.step_type === 'email' && (
              <div style={{ marginTop: 6 }}>
                {step.subject && <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text }}>Subject: {step.subject}</div>}
                {step.body && (
                  <div style={{
                    fontSize: 11, color: theme.colors.textLight, marginTop: 2, whiteSpace: 'pre-wrap',
                    maxHeight: expanded ? 'none' : 60, overflow: 'hidden',
                    position: 'relative',
                  }}>
                    {step.body}
                    {!expanded && hasLongContent && (
                      <div style={{
                        position: 'absolute', bottom: 0, left: 0, right: 0, height: 30,
                        background: 'linear-gradient(transparent, white)',
                      }} />
                    )}
                  </div>
                )}
                {hasLongContent && (
                  <button onClick={() => setExpanded(!expanded)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0',
                    fontSize: 10, fontWeight: 600, color: theme.colors.primary,
                  }}>
                    {expanded ? 'Show less' : 'Show full email...'}
                  </button>
                )}
              </div>
            )}
            {step.step_type === 'sms' && step.body && (
              <div style={{ marginTop: 6 }}>
                <div style={{
                  fontSize: 11, color: theme.colors.textLight, whiteSpace: 'pre-wrap',
                  maxHeight: expanded ? 'none' : 60, overflow: 'hidden',
                  position: 'relative',
                }}>
                  {step.body}
                  {!expanded && hasLongContent && (
                    <div style={{
                      position: 'absolute', bottom: 0, left: 0, right: 0, height: 30,
                      background: 'linear-gradient(transparent, white)',
                    }} />
                  )}
                </div>
                {hasLongContent && (
                  <button onClick={() => setExpanded(!expanded)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0',
                    fontSize: 10, fontWeight: 600, color: theme.colors.primary,
                  }}>
                    {expanded ? 'Show less' : 'Show full message...'}
                  </button>
                )}
              </div>
            )}
            {step.step_type === 'task' && (
              <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text, marginTop: 6 }}>
                {step.task_title || 'Untitled task'}
                {step.task_priority && <span style={{ fontSize: 10, marginLeft: 6, color: theme.colors.textLight }}>({step.task_priority})</span>}
              </div>
            )}
            {step.step_type === 'wait' && (
              <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 6 }}>
                Wait {delay || '—'} before continuing
              </div>
            )}
            {step.step_type === 'update_field' && (
              <div style={{ fontSize: 12, color: theme.colors.text, marginTop: 6 }}>
                Set <strong>{step.field_name}</strong> → <strong>{step.field_value}</strong>
              </div>
            )}
            {step.step_type === 'notify' && step.body && (
              <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 6 }}>{step.body}</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// Add Step Form
// ══════════════════════════════════════════════════════════════
function AddStepForm({ templateId, nextOrder, onAdded, onCancel, showToast }) {
  const [stepType, setStepType] = useState('email');
  const [delayDays, setDelayDays] = useState(0);
  const [delayHours, setDelayHours] = useState(0);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [taskTitle, setTaskTitle] = useState('');
  const [taskPriority, setTaskPriority] = useState('medium');
  const [fieldName, setFieldName] = useState('lead_status');
  const [fieldValue, setFieldValue] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const payload = {
        step_type: stepType,
        step_order: nextOrder,
        delay_days: parseInt(delayDays) || 0,
        delay_hours: parseInt(delayHours) || 0,
      };

      if (stepType === 'email') {
        payload.subject = subject;
        payload.body = body;
      } else if (stepType === 'sms' || stepType === 'whatsapp') {
        payload.body = body;
      } else if (stepType === 'task') {
        payload.task_title = taskTitle || 'Follow up';
        payload.task_priority = taskPriority;
        payload.body = body;
      } else if (stepType === 'update_field') {
        payload.field_name = fieldName;
        payload.field_value = fieldValue;
      } else if (stepType === 'notify') {
        payload.body = body;
      }

      const r = await fetch(`/api/action-plans?id=${templateId}&steps=1`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed');
      showToast?.('Step added');
      onAdded();
    } catch (e) {
      showToast?.(e.message);
    } finally { setSaving(false); }
  };

  const meta = STEP_TYPES.find(s => s.value === stepType);

  return (
    <div style={{
      padding: 16, borderRadius: 12,
      border: `2px solid ${theme.colors.primary}40`, background: `${theme.colors.primary}04`,
      marginTop: 8,
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: theme.colors.text, marginBottom: 10 }}>
        Add Step {nextOrder}
      </div>

      {/* Step type selector */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 12 }}>
        {STEP_TYPES.map(st => {
          const Icon = st.icon;
          const active = stepType === st.value;
          return (
            <button key={st.value} onClick={() => setStepType(st.value)} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '5px 10px', borderRadius: 6,
              border: active ? `2px solid ${st.color}` : `1px solid ${theme.colors.border}`,
              background: active ? `${st.color}12` : theme.colors.card,
              color: active ? st.color : theme.colors.text,
              fontSize: 10, fontWeight: 600, cursor: 'pointer',
            }}>
              <Icon size={11} /> {st.label}
            </button>
          );
        })}
      </div>

      {/* Delay */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
        <div>
          <label style={labelStyle()}>Wait (days)</label>
          <input type="number" min={0} value={delayDays} onChange={e => setDelayDays(e.target.value)}
            style={{ ...inputStyle(), width: 80 }} />
        </div>
        <div>
          <label style={labelStyle()}>Hours</label>
          <input type="number" min={0} max={23} value={delayHours} onChange={e => setDelayHours(e.target.value)}
            style={{ ...inputStyle(), width: 80 }} />
        </div>
      </div>

      {/* Type-specific fields */}
      {stepType === 'email' && (
        <>
          <div style={{ marginBottom: 8 }}>
            <label style={labelStyle()}>Subject</label>
            <input value={subject} onChange={e => setSubject(e.target.value)}
              placeholder="Hi {{first_name}}, just checking in…" style={inputStyle()} />
          </div>
          <div style={{ marginBottom: 8 }}>
            <label style={labelStyle()}>Body</label>
            <textarea value={body} onChange={e => setBody(e.target.value)}
              placeholder="Email body — supports {{first_name}}, {{agent_name}}, etc." rows={4}
              style={{ ...inputStyle(), resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
        </>
      )}
      {(stepType === 'sms' || stepType === 'whatsapp') && (
        <div style={{ marginBottom: 8 }}>
          <label style={labelStyle()}>Message</label>
          <textarea value={body} onChange={e => setBody(e.target.value)}
            placeholder="Hi {{first_name}}! {{agent_name}} here from {{brokerage_name}}…" rows={3}
            style={{ ...inputStyle(), resize: 'vertical', fontFamily: 'inherit' }} />
        </div>
      )}
      {stepType === 'task' && (
        <>
          <div style={{ marginBottom: 8 }}>
            <label style={labelStyle()}>Task Title</label>
            <input value={taskTitle} onChange={e => setTaskTitle(e.target.value)}
              placeholder="Call {{first_name}} to discuss…" style={inputStyle()} />
          </div>
          <div style={{ marginBottom: 8 }}>
            <label style={labelStyle()}>Priority</label>
            <select value={taskPriority} onChange={e => setTaskPriority(e.target.value)} style={inputStyle()}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
        </>
      )}
      {stepType === 'update_field' && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle()}>Field</label>
            <select value={fieldName} onChange={e => setFieldName(e.target.value)} style={inputStyle()}>
              <option value="lead_status">Lead Status</option>
              <option value="lead_type">Lead Type</option>
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle()}>New Value</label>
            <input value={fieldValue} onChange={e => setFieldValue(e.target.value)}
              placeholder="e.g., nurturing" style={inputStyle()} />
          </div>
        </div>
      )}
      {stepType === 'notify' && (
        <div style={{ marginBottom: 8 }}>
          <label style={labelStyle()}>Notification Message</label>
          <input value={body} onChange={e => setBody(e.target.value)}
            placeholder="Reminder: Follow up with {{first_name}}" style={inputStyle()} />
        </div>
      )}

      {/* Merge field help */}
      <div style={{ fontSize: 10, color: theme.colors.textLight, marginBottom: 12 }}>
        Merge fields: {'{{first_name}}'}, {'{{last_name}}'}, {'{{agent_name}}'}, {'{{brokerage_name}}'}, {'{{lead_source}}'}, {'{{budget}}'}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} style={{
          padding: '7px 14px', borderRadius: 8, border: `1px solid ${theme.colors.border}`,
          background: theme.colors.card, fontSize: 12, fontWeight: 600, cursor: 'pointer',
          color: theme.colors.text,
        }}>Cancel</button>
        <button onClick={save} disabled={saving} style={{
          padding: '7px 14px', borderRadius: 8, border: 'none',
          background: theme.colors.primary, color: 'white', fontSize: 12, fontWeight: 600,
          cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.5 : 1,
          display: 'inline-flex', alignItems: 'center', gap: 5,
        }}>
          {saving ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Plus size={12} />}
          Add Step
        </button>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// Enrollment Row
// ══════════════════════════════════════════════════════════════
function EnrollmentRow({ enrollment: e, onStatusChange }) {
  const contact = e.contact || {};
  const name = [contact.first_name, contact.last_name].filter(Boolean).join(' ') || 'Unknown';
  const template = e.template || {};
  const progress = template.step_count > 0 ? Math.round((e.steps_completed / template.step_count) * 100) : 0;

  const statusColors = {
    active: theme.colors.success,
    paused: theme.colors.warning,
    completed: theme.colors.primary,
    stopped_reply: '#8B5CF6',
    stopped_deal: '#06B6D4',
    cancelled: theme.colors.danger,
  };
  const statusColor = statusColors[e.status] || theme.colors.textLight;

  return (
    <div style={{
      padding: '10px 14px', borderRadius: 10,
      border: `1px solid ${theme.colors.border}`, background: theme.colors.card,
      display: 'flex', alignItems: 'center', gap: 12,
    }}>
      {/* Contact */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>{name}</div>
        <div style={{ fontSize: 11, color: theme.colors.textLight }}>
          Step {e.current_step_order} of {template.step_count || '?'} · {e.steps_completed} completed
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ width: 60 }}>
        <div style={{ height: 4, borderRadius: 2, background: theme.colors.bg, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${progress}%`, background: statusColor, borderRadius: 2, transition: 'width 0.3s' }} />
        </div>
        <div style={{ fontSize: 9, textAlign: 'center', marginTop: 2, color: theme.colors.textLight }}>{progress}%</div>
      </div>

      {/* Status badge */}
      <span style={{
        padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
        background: `${statusColor}14`, color: statusColor, textTransform: 'capitalize',
      }}>
        {e.status.replace(/_/g, ' ')}
      </span>

      {/* Actions */}
      {e.status === 'active' && (
        <button onClick={() => onStatusChange(e.id, 'paused')} title="Pause" style={{
          background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.warning, padding: 4,
        }}>
          <Pause size={14} />
        </button>
      )}
      {e.status === 'paused' && (
        <button onClick={() => onStatusChange(e.id, 'active')} title="Resume" style={{
          background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.success, padding: 4,
        }}>
          <Play size={14} />
        </button>
      )}
      {(e.status === 'active' || e.status === 'paused') && (
        <button onClick={() => onStatusChange(e.id, 'cancelled')} title="Cancel" style={{
          background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.danger, padding: 4,
        }}>
          <X size={14} />
        </button>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// Helpers
// ══════════════════════════════════════════════════════════════
function formatDelay(step) {
  const parts = [];
  if (step.delay_days > 0) parts.push(`${step.delay_days}d`);
  if (step.delay_hours > 0) parts.push(`${step.delay_hours}h`);
  if (step.delay_minutes > 0) parts.push(`${step.delay_minutes}m`);
  return parts.length ? `after ${parts.join(' ')}` : 'immediately';
}

function toolBtn() {
  return {
    padding: '7px 10px', borderRadius: 8,
    border: `1px solid ${theme.colors.border}`, background: theme.colors.card,
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
    color: theme.colors.text, fontSize: 11, fontWeight: 600,
  };
}

function labelStyle() {
  return {
    display: 'block', fontSize: 10, fontWeight: 700, color: theme.colors.textLight,
    textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4,
  };
}

function inputStyle() {
  return {
    width: '100%', padding: '8px 10px', borderRadius: 8, boxSizing: 'border-box',
    border: `1px solid ${theme.colors.border}`, fontSize: 13,
    color: theme.colors.text, background: theme.colors.card,
  };
}
